import { Component, ViewChild, OnInit } from "@angular/core";
import {
  NavController,
  Events,
  NavParams,
  ModalController,
  AlertController
} from "ionic-angular";
import { Store } from "@ngrx/store";
import { AppState } from "../../app/models/app.state";
import { FhirService } from "../../app/services/fhir.service";
import { TranslateService } from "@ngx-translate/core";
import { DashboardService } from "../../app/services/dashboard.service";
import {
  DIAPROFIL_QUESTIONNAIRE_UUID,
  supportedDiabetesTypes,
  ActionType,
  actionTypes,
  diaprofilTopics
} from "../../app/models/types";
import { ISupportAction, SupportAction } from "../../app/models/support-action";
import { _i18n_str } from "../../assets/i18n/non-static-ids";
import { SupportActionsService } from "../../app/services/support-actions.service";
import { ManageSupportActionModal } from "../../modals/manage-support-action.modal/manage-support-action.modal";
import { WidgetConfig } from "@zitelab/ionic-fhir-questionnaires/dist/models/widget";
import { OptionObject } from "@zitelab/ionic-fhir-questionnaires";

@Component({
  selector: "support-actions-admin-page",
  templateUrl: "support-actions-admin.page.html"
})
export class SupportActionsAdminPage implements OnInit {
  @ViewChild("supportActionsListing") table: any;
  localizedTableMessages: { [key: string]: string };
  consultationTopicOptions: OptionObject[];
  supportActions: SupportAction[];
  supportedDiabetesTypes = supportedDiabetesTypes;
  actionTypes = actionTypes;
  actionTopics = diaprofilTopics;
  selectableWidgets: WidgetConfig<any>[] = [];
  tableFilters: {
    title: string;
    providerName: string;
    diabetesType: string;
    actionType: "ALL" | ActionType;
    actionTopics: string[];
    consultationTopicIdsVersion2: string[];
    questionIds: string[];
  } = {
    title: null,
    providerName: null,
    diabetesType: "ALL",
    actionType: "ALL",
    actionTopics: null,
    consultationTopicIdsVersion2: null,
    questionIds: null
  };
  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public events: Events,
    public store: Store<AppState>,
    public fhir: FhirService,
    public support: SupportActionsService,
    public dashboard: DashboardService,
    public translate: TranslateService,
    public modalCtrl: ModalController,
    public alertCtrl: AlertController
  ) {
    this.store
      .select(state => state.supportActions)
      .subscribe(supportActions => {
        const allSupportActions = supportActions.map(
          action => new SupportAction(action)
        );
        this.filterRows(this.tableFilters, allSupportActions); // sets this.supportActions
      });
    this.localizedTableMessages = {
      // Message to show when array is presented
      // but contains no values
      emptyMessage: translate.instant(
        _i18n_str("SUPPORT_ACTION_LISTING.MESSAGES.NO_DATA_TO_DISPLAY")
      ),
      // Footer total message
      totalMessage: translate.instant(
        _i18n_str("SUPPORT_ACTION_LISTING.MESSAGES.TOTAL")
      )
    };
    this.consultationTopicOptions = this.dashboard.getConsultationTopicOptions();
    if (this.dashboard.widgets === null) {
      const questionnaireResource = this.fhir.resolveReference(
        `Questionnaire/${DIAPROFIL_QUESTIONNAIRE_UUID}`
      ) as fhir.Questionnaire;
      this.dashboard.setGenericProWidgets(questionnaireResource);
    }
    this.selectableWidgets = this.dashboard.widgets.filter(
      widget => widget.widgetType !== "LabelWidget" && widget.prefix !== null
    );
  }

  triggerSupportActionModal($event, supportAction?: ISupportAction) {
    if (this.dashboard.widgets === null) {
      const questionnaireResource = this.fhir.resolveReference(
        `Questionnaire/${DIAPROFIL_QUESTIONNAIRE_UUID}`
      ) as fhir.Questionnaire;
      this.dashboard.setGenericProWidgets(questionnaireResource);
    }
    const navParams =
      supportAction !== undefined ? { supportAction, modalType: "EDIT" } : {};
    const modal = this.modalCtrl.create(ManageSupportActionModal, navParams, {
      cssClass: "large-modal",
      enableBackdropDismiss: false
    });
    modal.present();
  }

  triggerDeleteSupportActionAlert(event, supportAction: ISupportAction) {
    const prompt = this.alertCtrl.create({
      title: this.translate.instant(
        "SUPPORT_ACTIONS_ADMIN_PAGE.DELETE_SUPPORT_ACTION_ALERT.HEADER"
      ),
      message: this.translate.instant(
        "SUPPORT_ACTIONS_ADMIN_PAGE.DELETE_SUPPORT_ACTION_ALERT.MESSAGE"
      ),
      buttons: [
        {
          text: this.translate.instant(
            "SUPPORT_ACTIONS_ADMIN_PAGE.DELETE_SUPPORT_ACTION_ALERT.OK_BTN"
          ),
          handler: data => {
            this.support.deleteSupportAction(supportAction);
          }
        },
        {
          text: this.translate.instant(
            "SUPPORT_ACTIONS_ADMIN_PAGE.DELETE_SUPPORT_ACTION_ALERT.CANCEL_BTN"
          ),
          handler: data => {
            //
          }
        }
      ]
    });
    prompt.present();
  }

  ngOnInit() {
    if (this.dashboard.widgets === null) {
      const questionnaireResource = this.fhir.resolveReference(
        `Questionnaire/${DIAPROFIL_QUESTIONNAIRE_UUID}`
      ) as fhir.Questionnaire;
      this.dashboard.setGenericProWidgets(questionnaireResource);
    }
  }

  selectProItemFilter(term: string, item: WidgetConfig<any>) {
    return `${item.prefix} ${item.label}`.indexOf(term) > -1;
  }

  getConsultationTopicOptionObject(topicId): OptionObject {
    return this.consultationTopicOptions.find(
      option => option.code === topicId
    );
  }

  getQuestionConfigBasedOnId(questionId: string): WidgetConfig<any> {
    if (this.dashboard.widgets === null) {
      return null;
    }
    const questionWidget = this.dashboard.widgets.find(
      widget => widget.name === questionId
    );
    return questionWidget !== undefined ? questionWidget : null;
  }

  ionViewWillEnter() {
    this.support.getSupportActions();
  }

  onDetailToggle(event) {
    console.log("Detail Toggled", event);
  }

  toggleExpandRow(row) {
    this.table.rowDetail.toggleExpandRow(row);
  }
  filterRows(filterValues, allSupportActions = this.support.supportActions) {
    const hasIntersection = (a: string[], b: string[]) => {
      if (a === null || b === null) {
        return false;
      }
      const setA = new Set(a);
      const setB = new Set(b);
      return [...setA].filter(id => setB.has(id)).length > 0;
    };
    const filters = {
      title: (action: SupportAction, filterBy: string) => {
        return (
          action.title
            .toLocaleLowerCase()
            .indexOf(filterBy.toLocaleLowerCase()) > -1
        );
      },
      providerName: (action: SupportAction, filterBy: string) => {
        return (
          action.providerName
            .toLocaleLowerCase()
            .indexOf(filterBy.toLocaleLowerCase()) > -1
        );
      },
      diabetesType: (action: SupportAction, filterBy: string) =>
        filterBy === "ALL" ||
        action.supportForDiabetesType === "ALL" ||
        action.supportForDiabetesType === filterBy,
      actionType: (action: SupportAction, filterBy: string) =>
        filterBy === "ALL" || action.actionType === filterBy,
      actionTopics: (action: SupportAction, filterBy: string[]) =>
        hasIntersection(action.actionTopics, filterBy),
      consultationTopicIdsVersion2: (
        action: SupportAction,
        filterBy: string[]
      ) => hasIntersection(action.consultationTopicIdsVersion2, filterBy),
      questionIds: (action: SupportAction, filterBy: string[]) =>
        hasIntersection(action.questionIds, filterBy)
    };
    const activeFilters = Object.keys(filters).filter(
      (keyToFilterOn, index) => {
        const filterValue = filterValues[keyToFilterOn];
        return (
          filterValue !== null &&
          filterValue !== "ALL" &&
          !(Array.isArray(filterValue) && filterValue.length === 0)
        );
      }
    );
    const runFilters = (action: SupportAction) => {
      let result = true;
      for (const filter of activeFilters) {
        const filterFunction = filters[filter];
        const filterBy = filterValues[filter];
        const filterResult = filterFunction(action, filterBy);
        if (filterResult === false) {
          result = false;
          break;
        }
      }
      return result;
    };
    const filteredSupportActions = allSupportActions.filter(patient =>
      runFilters(patient)
    );
    this.supportActions = filteredSupportActions;
  }
}



// WEBPACK FOOTER //
// ./src/pages/support-actions-admin.page/support-actions-admin.page.ts